import { yupResolver } from '@hookform/resolvers/yup';
import React, { useEffect, useState } from 'react';
import { Form } from 'react-bootstrap';
import { useCookies } from 'react-cookie';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import * as Yup from 'yup';

import { useAppSelector } from '@/app/hook';
import { useUpdateAiWidgetAppearanceMutation, useUpdateMcBotWidgetAppearanceMutation } from '@/app/services/chatbot-rtk.services';
import { BannerHelper } from '@/components/common/BannerToast';
import { PSButton } from '@/components/ui/button';
import { selectAppearanceState } from '@/features/appearance/appearanceSlice';
import { AppearanceType } from '@/features/appearance/types';
import { BotTypeEnum } from '@/utils/BotTypeEnum';

import usePermission from '../../../../hooks/usePermission';
import ProBadge from '../../../ui/ProBadge';

function BotSessionLimit() {
  const { t } = useTranslation();

  const currentValidationSchema = Yup.object({
    sessionLimit: Yup.string().nullable().max(6, 'Please enter no more than 6 characters'),
  });

  const methods = useForm({
    shouldUnregister: false,
    resolver: currentValidationSchema ? yupResolver(currentValidationSchema) : undefined,
    mode: 'all',
  });
  const [updateAiWidgetAppearance, UpdateAiWidgetAppearanceResults] = useUpdateAiWidgetAppearanceMutation();
  const [updateMcBotWidgetAppearance, updateMcBotWidgetAppearanceResults] = useUpdateMcBotWidgetAppearanceMutation();

  const appearanceState = useAppSelector(selectAppearanceState);

  const [, removeCookie] = useCookies([`Louise_Widget_${appearanceState.id}`]);
  const [, setClonedData] = useState<any>();

  const permissions = usePermission();

  useEffect(() => {
    if (appearanceState.bot_code) {
      setClonedData(appearanceState);
    }
    if (appearanceState?.configs?.sessionLimit && appearanceState?.configs?.sessionLimit > -1)
      methods.setValue('sessionLimit', appearanceState.configs?.sessionLimit);
    return () => {
      setClonedData(undefined);
    };
  }, [appearanceState.bot_code]);

  const onSubmit = (data: any) => {
    const clonedData: AppearanceType = JSON.parse(JSON.stringify(appearanceState.configs));
    clonedData.sessionLimit =
      methods.getValues('sessionLimit') == null || methods.getValues('sessionLimit') == undefined ? -1 : parseInt(methods.getValues('sessionLimit') || -1);
    if (appearanceState.bot_code !== BotTypeEnum.TEXT_SCENARIO && appearanceState.bot_code !== BotTypeEnum.VIDEO_SCENARIO)
      updateAiWidgetAppearance({
        id: appearanceState.id,
        data: { settings: JSON.stringify(clonedData) },
      })
        .unwrap()
        .then(() => {
          toast.success('Successfully updated');
          removeCookie(`Louise_Widget_${appearanceState.id}`, {
            path: '/',
          });
          //handleCancelAppearanceConfig();
          setClonedData(appearanceState);
        })
        .catch(err => {
          toast.error(err.data.message || err.message);
        });
    else
      updateMcBotWidgetAppearance({
        id: appearanceState.id,
        data: { settings: JSON.stringify(clonedData) },
      })
        .unwrap()
        .then(() => {
          toast.success('Successfully updated');
          removeCookie(`Louise_Widget_${appearanceState.id}`, {
            path: '/',
          });
          //handleCancelAppearanceConfig();
          setClonedData(appearanceState);
        })
        .catch(err => {
          if (err.status < 500) toast.warn(err?.data?.message || err.message);
          if (err.status >= 500) toast.error(err?.data?.message || err.message);
        });
  };

  return (
    <div className="lg:tw-p-6 tw-p-2 tw-bg-white tw-rounded-2xl tw-shadow-[0px_1px_2px_0px_rgba(0,0,0,0.06)] tw-flex-col tw-mt-6 ">
      <form onSubmit={methods.handleSubmit(onSubmit)}>
        <div className="tw-self-stretch tw-flex-col tw-justify-start tw-items-start tw-gap-5 tw-flex tw-mb-5">
          <div className="tw-self-stretch tw-flex-col tw-justify-start tw-items-start tw-gap-2 tw-flex">
            <BannerHelper message={t('HelpBanner/Sessions/hint')} />
            <div className="tw-flex tw-flex-row  tw-leading-tight tw-text-sm tw-font-normal tw-items-center">
              <div className="tw-self-stretch tw-text-gray-900 tw-text-2xl tw-font-semibold tw-font-['Lexend'] tw-leading-loose">
                {t('BotSessionLimit/key-01')}{' '}
              </div>
              {!permissions.settingsPage.widget.customization.sessionLimit && (
                <div className="  tw-max-h-[24px] tw-ml-4">
                  <ProBadge />
                </div>
              )}
            </div>
          </div>
          <div className="md:tw-max-w-[357px] tw-w-full tw-justify-start tw-items-start tw-gap-2 tw-flex tw-flex-col">
            <Form.Group className="tw-w-full">
              <Form.Control
                isInvalid={!!methods.formState.errors.sessionLimit}
                disabled={!permissions.settingsPage.widget.customization.sessionLimit}
                value={methods.watch('sessionLimit')}
                onChange={e => {
                  const value = e.target.value;
                  // Allow only up to 6 digits and numeric values
                  if (/^\d*$/.test(value) && value.length <= 6) {
                    methods.setValue('sessionLimit', value, {
                      shouldValidate: true,
                      shouldDirty: true,
                    });
                  }
                }}
                placeholder={t('BotSessionLimit/key-02')}
                type={'number'}
                className="tw-h-10 tw-px-4 tw-py-2.5 tw-bg-white tw-rounded-md tw-shadow-sm tw-border tw-border-gray-300 tw-justify-start tw-items-center tw-inline-flex"
              />
              <Form.Control.Feedback type="invalid" className="d-block font-poppins">
                {methods?.formState?.errors?.sessionLimit?.message as string}
              </Form.Control.Feedback>
            </Form.Group>
          </div>
        </div>

        <hr className="tw-w-full" />

        <div className="tw-flex tw-flex-row  tw-items-end tw-w-full tw-justify-end">
          <PSButton
            id="btn-submit-session-limit"
            variant="secondary"
            type="submit"
            disabled={
              !permissions.settingsPage.widget.customization.sessionLimit ||
              UpdateAiWidgetAppearanceResults.isLoading ||
              updateMcBotWidgetAppearanceResults.isLoading ||
              !methods.formState.isDirty
            }
            title={t('Wizard/general/key7')}
            isLoading={UpdateAiWidgetAppearanceResults.isLoading || updateMcBotWidgetAppearanceResults.isLoading}
          />
        </div>
      </form>
    </div>
  );
}

export default BotSessionLimit;
